import {
	EnvironmentOutlined,
	HomeOutlined,
	IdcardOutlined,
	MailOutlined,
	PercentageOutlined,
	PhoneOutlined,
	TagOutlined,
	UserOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { getAperturaActiva } from "../../services/cajaEstadoService";
import { getCajaByNoOrId } from "../../services/cajaService";
import { addClient, updateClient } from "../../services/clientService";
import { getTPVByNoOrId } from "../../services/tpvService";

const ClientForm = ({ onClientAdded, onClientUpdated, clientToEdit, initialData, skipAutoClearOnTipoChange = false }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [isEditing, setIsEditing] = useState(false);

	// Observar cambios en el tipo de documento
	const tipoDocumento = Form.useWatch('Tipo_Documento_SrI', form);

	// Función para validar el número de identificación según el tipo
	const validateIdentificationNumber = (_, value) => {
		if (!value) {
			return Promise.reject(new Error('Por favor ingrese el número de identificación'));
		}

		const currentTipo = form.getFieldValue('Tipo_Documento_SrI') || 'Cedula';

		switch (currentTipo) {
			case 'Cedula':
				if (!/^\d{10}$/.test(value)) {
					return Promise.reject(new Error('La cédula debe tener exactamente 10 dígitos numéricos'));
				}
				break;
			case 'RUC':
				if (!/^\d{13}$/.test(value)) {
					return Promise.reject(new Error('El RUC debe tener exactamente 13 dígitos numéricos'));
				}
				break;
			case 'Pasaporte':
				if (!/^[A-Za-z0-9]+$/.test(value) || value.length < 6 || value.length > 20) {
					return Promise.reject(new Error('El pasaporte debe ser alfanumérico entre 6 y 20 caracteres'));
				}
				break;
			default:
				break;
		}

		return Promise.resolve();
	};

	// Función para manejar la entrada de caracteres según el tipo de documento
	const handleIdentificationInput = (e) => {
		const currentTipo = form.getFieldValue('Tipo_Documento_SrI') || 'Cedula';
		let value = e.target.value;

		switch (currentTipo) {
			case 'Cedula':
				// Solo números, máximo 10 dígitos
				value = value.replace(/[^0-9]/g, '').slice(0, 10);
				break;
			case 'RUC':
				// Solo números, máximo 13 dígitos
				value = value.replace(/[^0-9]/g, '').slice(0, 13);
				break;
			case 'Pasaporte':
				// Alfanumérico, máximo 20 caracteres
				value = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 20);
				break;
			default:
				break;
		}

		form.setFieldValue('Numero_Identificacion_fiscal', value);
	};

	// Función para obtener el placeholder según el tipo de documento
	const getIdentificationPlaceholder = () => {
		switch (tipoDocumento) {
			case 'Cedula':
				return 'Ingrese 10 dígitos de la cédula';
			case 'RUC':
				return 'Ingrese 13 dígitos del RUC';
			case 'Pasaporte':
				return 'Ingrese el número del pasaporte';
			default:
				return 'Número de identificación';
		}
	};

	// Limpiar campo de identificación cuando cambie el tipo de documento (evitable por prop)
	useEffect(() => {
		if (tipoDocumento && !isEditing && !skipAutoClearOnTipoChange) {
			form.setFieldValue('Numero_Identificacion_fiscal', '');
		}
	}, [tipoDocumento, form, isEditing, skipAutoClearOnTipoChange]);

	useEffect(() => {
		const getCurrentApertura = async () => {
			const apertura = await getAperturaActiva()
			if (!apertura) return
			const caja = await getCajaByNoOrId(apertura.Caja)
			if (!caja) return
			const tpv = await getTPVByNoOrId(caja.TPV)
			if (!tpv) return
			// No sobreescribir País si ya viene precargado
			if (tpv.pais && !form.getFieldValue('Pais')) {
				form.setFieldsValue({
					Pais: tpv.pais,
				})
			}
		}
		getCurrentApertura()
	}, [form])

	// Precargar datos iniciales desde la factura cuando se crea un cliente nuevo
	useEffect(() => {
		if (!isEditing && initialData) {
			form.setFieldsValue({
				name: initialData.name || '',
				Direccion: initialData.Direccion || '',
				Direccion_2: initialData.Direccion_2 || '',
				Poblacion: initialData.Poblacion || '',
				Cod_Postal: initialData.Cod_Postal || '',
				Pais: initialData.Pais || form.getFieldValue('Pais') || '',
				Telefono: initialData.Telefono || '',
				Mobil: initialData.Mobil || '',
				E_Mail: initialData.E_Mail || '',
				Customer_Price_Group: initialData.Customer_Price_Group || '',
				Contacto: initialData.Contacto || '',
				Tipo_Documento_SrI: initialData.Tipo_Documento_SrI || 'Cedula',
				Numero_Identificacion_fiscal: initialData.Numero_Identificacion_fiscal || '',
				POS_Discount: initialData.POS_Discount || 0,
			});
		}
	}, [initialData, isEditing, form]);


	// Cargar datos del cliente si estamos en modo edición
	useEffect(() => {
		if (clientToEdit) {
			form.setFieldsValue({
				name: clientToEdit.name,
				Direccion: clientToEdit.Direccion,
				Direccion_2: clientToEdit.Direccion_2,
				Poblacion: clientToEdit.Poblacion,
				Cod_Postal: clientToEdit.Cod_Postal,
				Pais: clientToEdit.Pais,
				Telefono: clientToEdit.Telefono,
				Mobil: clientToEdit.Mobil,
				E_Mail: clientToEdit.E_Mail,
				Customer_Price_Group: clientToEdit.Customer_Price_Group,
				Contacto: clientToEdit.Contacto,
				Tipo_Documento_SrI: clientToEdit.Tipo_Documento_SrI || "Cedula",
				Numero_Identificacion_fiscal: clientToEdit.Numero_Identificacion_fiscal,
				POS_Discount: clientToEdit.POS_Discount || 0,
			});
			setIsEditing(true);
		} else {
			form.resetFields();
			setIsEditing(false);
		}
	}, [clientToEdit, form]);

	const onFinish = async (values) => {
		try {
			// Limpiar error previo
			setError("");
			setLoading(true);

			if (isEditing) {
				// Actualizar cliente existente
				const updatedClient = await updateClient(clientToEdit.id, {
					name: values.name,
					Direccion: values.Direccion,
					Direccion_2: values.Direccion_2,
					Poblacion: values.Poblacion,
					Cod_Postal: values.Cod_Postal,
					Pais: values.Pais,
					Telefono: values.Telefono,
					Mobil: values.Mobil,
					E_Mail: values.E_Mail,
					Customer_Price_Group: values.Customer_Price_Group,
					Contacto: values.Contacto,
					Tipo_Documento_SrI: values.Tipo_Documento_SrI,
					Numero_Identificacion_fiscal: values.Numero_Identificacion_fiscal,
					POS_Discount: values.POS_Discount || 0,
					pendingSync: 1,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Cliente actualizado correctamente");

				// Notificar al componente padre
				if (onClientUpdated) onClientUpdated(updatedClient);
			} else {
				// Guardar cliente en IndexedDB
				const result = await addClient({
					name: values.name,
					Direccion: values.Direccion,
					Direccion_2: values.Direccion_2,
					Poblacion: values.Poblacion,
					Cod_Postal: values.Cod_Postal,
					Pais: values.Pais,
					Telefono: values.Telefono,
					Mobil: values.Mobil,
					E_Mail: values.E_Mail,
					Customer_Price_Group: values.Customer_Price_Group,
					Contacto: values.Contacto,
					Tipo_Documento_SrI: values.Tipo_Documento_SrI,
					Numero_Identificacion_fiscal: values.Numero_Identificacion_fiscal,
					POS_Discount: values.POS_Discount || 0,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Cliente registrado correctamente");

				// Limpiar formulario
				form.resetFields();

				// Notificar al componente padre
				if (onClientAdded) onClientAdded(result);
			}
		} catch (error) {
			// Mostrar error específico
			setError(error.message || "Error al procesar el cliente");
			console.error("Error al procesar cliente:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setIsEditing(false);
		if (onClientUpdated) onClientUpdated(null);
	};

	return (
		<div className="client-form-container">
			<h2>{isEditing ? "Editar Cliente" : "Registro de Cliente"}</h2>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Form
				form={form}
				name="client-form"
				onFinish={onFinish}
				layout="vertical"
				scrollToFirstError
			>
				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="name"
							label="Nombre"
							rules={[
								{
									required: true,
									message: "Por favor ingrese el nombre del cliente",
								},
							]}
						>
							<Input
								prefix={<UserOutlined />}
								placeholder="Ingrese el nombre del cliente"
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							name="Tipo_Documento_SrI"
							label="Tipo Documento SrI"
							initialValue="Cedula"
							rules={[
								{
									required: true,
									message: "Por favor seleccione el tipo de documento",
								},
							]}
						>
							<Select
								placeholder="Seleccione el tipo de documento"
								options={[
									{ value: "RUC", label: "RUC" },
									{ value: "Cedula", label: "Cédula" },
									{ value: "Pasaporte", label: "Pasaporte" },
								]}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="Numero_Identificacion_fiscal"
							label="Número de cédula/RUC/Passaporte"
							rules={[
								{
									validator: validateIdentificationNumber,
								},
							]}
						>
							<Input
								prefix={<IdcardOutlined />}
								placeholder={getIdentificationPlaceholder()}
								onChange={handleIdentificationInput}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Información de contacto</Divider>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="E_Mail"
							label="Email"
							rules={[
								{
									type: "email",
									message: "El formato del email no es válido",
								},
								{
									required: true,
									message: "Por favor ingrese un email",
								},
							]}
						>
							<Input
								prefix={<MailOutlined />}
								placeholder="Correo electrónico"
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={12}>
						<Form.Item name="Customer_Price_Group" label="Grupo de precios">
							<Input prefix={<TagOutlined />} placeholder="Grupo de precios" />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="Telefono"
							label="Teléfono"
							rules={[
								{
									required: true,
									message: "Por favor ingrese un número de teléfono",
								},
								{
									pattern: /^[0-9]+$/,
									message: "El teléfono solo puede contener números",
								},
							]}
						>
							<Input
								prefix={<PhoneOutlined />}
								placeholder="Teléfono fijo"
								onKeyPress={(e) => {
									// Solo permitir números
									if (!/[0-9]/.test(e.key)) {
										e.preventDefault();
									}
								}}
								onChange={(e) => {
									// Filtrar solo números
									const value = e.target.value.replace(/[^0-9]/g, '');
									form.setFieldValue('Telefono', value);
								}}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							name="Pais"
							label="País"
							rules={[{ required: true, message: "Ingrese un país" }]}
						>
							<Input prefix={<EnvironmentOutlined />} placeholder="País" />
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Dirección</Divider>

				<Row gutter={16}>
					<Col xs={24}>
						<Form.Item
							name="Direccion"
							label="Dirección"
							rules={[
								{
									required: true,
									message: "Por favor ingrese la dirección del cliente",
								},
							]}
						>
							<Input
								prefix={<HomeOutlined />}
								placeholder="Dirección principal"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24}>
						<Form.Item name="Direccion_2" label="Envio a Colonia 2">
							<Input
								prefix={<HomeOutlined />}
								placeholder="Dirección secundaria o información adicional de envío"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24}>
						<Form.Item name="Poblacion" label="Ciudad">
							<Input prefix={<EnvironmentOutlined />} placeholder="Ciudad" />
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Descuento</Divider>

				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="POS_Discount"
							label="Descuento TPV (%)"
							initialValue={0}
							rules={[
								{
									type: "number",
									min: 0,
									max: 100,
									message: "El descuento debe estar entre 0 y 100",
								},
							]}
						>
							<InputNumber

								disabled={true}
								prefix={<PercentageOutlined />}
								placeholder="Descuento TPV"
								min={0}
								max={100}
								precision={2}
								step={1}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item>
					<div style={{ display: "flex", gap: "10px" }}>
						<Button type="primary" htmlType="submit" loading={loading} block>
							{isEditing ? "Actualizar Cliente" : "Registrar Cliente"}
						</Button>

						{isEditing && (
							<Button onClick={handleCancel} block>
								Cancelar
							</Button>
						)}
					</div>
				</Form.Item>
			</Form>
		</div>
	);
};

export default ClientForm;
